import {
  LocalPreference,
  LocalPreferences,
  MapProvider,
  MapType,
  NewUserPermission,
  Tenant,
  UserCDN,
  UserClaim,
  UserPreferences,
  UserProfile,
  WithNull,
} from '@mti/multitenant-ui-shared';
import { PayloadAction } from '@reduxjs/toolkit';
import { call, delay, put, takeLatest } from 'redux-saga/effects';
import { request, ResponseError } from 'utils/request';
import { LogoutRequest } from '../Header/types';
import * as Auth from './auth';
import { actions } from './slice';
import {
  AuthenticationError,
  AuthErrorMessage,
  NotAuthorisedError,
  TenantIDWithExtension,
  UserWithTenant,
} from './types';

const BASE_URL_QUERYOPERATOR = process.env.REACT_APP_QUERYOPERATOR_API_ENDPOINT;
const BASE_URL_TENANT = process.env.REACT_APP_TENANT_API_ENDPOINT;
const BASE_URL_IDENTITY = process.env.REACT_APP_IDENTITY_API_ENDPOINT;
const BASE_URL_QUERY_OPERATOR =
  process.env.REACT_APP_QUERY_OPERATOR_API_ENDPOINT;
const BASE_URL_USER = process.env.REACT_APP_USER_API_ENDPOINT;
const API_KEY = process.env.REACT_APP_API_KEY;
const TOKEN_AUD = process.env.REACT_APP_MSAL_TOKEN_AUD;
const TOKEN_ISS = process.env.REACT_APP_MSAL_TOKEN_ISS;
const DEFAULT_MAP_PROVIDER: MapProvider = {
  mapProviderType: MapType[process.env.REACT_APP_DEFAULT_MAP_TYPE ?? 'GOOGLE'],
  browserKey: process.env.REACT_APP_DEFAULT_MAP_KEY ?? '',
};

export function* login(action: PayloadAction<TenantIDWithExtension>) {
  try {
    const { tenantId, operatorExtension } = action.payload;
    if (!tenantId) {
      yield put(actions.loginError(AuthErrorMessage.INVALID_TENANTID));
      return;
    }

    let userProfile: UserProfile = yield Auth.login();

    if (!userProfile) throw new AuthenticationError('Authentication Failed.');

    const [tenant, userPermissions, userCDNs, userPreferences] =
      yield fetchTenantAndUserPermissions(userProfile, tenantId);

    if (userProfile) {
      userProfile.tenantId = tenant?.tenantId ?? '';
      userProfile.operatorExtension = operatorExtension;
      userProfile.mapProvider = tenant?.mapProvider ?? DEFAULT_MAP_PROVIDER;

      //Save operatorExtension into local storage
      LocalPreferences.set(
        LocalPreference.LOCAL_PROFILE_OPT_EXTENSION_TOKEN,
        operatorExtension,
      );

      yield put(
        actions.loginSuccess({
          user: {
            userProfile: userProfile,
            userPermissions: userPermissions ?? [],
            userCDNs: userCDNs,
            userPreferences,
          },
          tenant: tenant,
          refresh: false,
        }),
      );
    } else {
      yield put(actions.loginError(AuthErrorMessage.AUTHENTICATION_FAILED));
    }
  } catch (error) {
    console.error(error.message);
    const message =
      error instanceof NotAuthorisedError
        ? AuthErrorMessage.NO_USER
        : AuthErrorMessage.AUTHENTICATION_FAILED;
    yield put(actions.loginError(message));
  }
}

export function* logout(action: PayloadAction<LogoutRequest | undefined>) {
  try {
    if (action.payload) yield unregisterUser(action.payload);
    LocalPreferences.set(LocalPreference.LOCAL_PROFILE_OPT_EXTENSION_TOKEN, '');
    yield Auth.logout();
  } catch (error) {
    console.error(error.message);
    yield put(actions.loginError(AuthErrorMessage.AUTHENTICATION_FAILED));
  }
}

export function* checkAuth(
  action: PayloadAction<{ tenantId: string | undefined; refresh: boolean }>,
) {
  try {
    let userProfile: UserProfile = yield Auth.getAuthenticatedUser();
    const { tenantId, refresh } = action.payload;

    if (!userProfile) throw new AuthenticationError('Authentication Failed.');
    const [tenant, userPermissions, userCDNs, userPreferences] =
      yield fetchTenantAndUserPermissions(userProfile, tenantId);

    if (userProfile) {
      userProfile.tenantId = tenant?.tenantId ?? '';
      userProfile.operatorExtension = LocalPreferences.getString(
        LocalPreference.LOCAL_PROFILE_OPT_EXTENSION_TOKEN,
      );

      if (tenant && tenant.mapProvider)
        userProfile.mapProvider = tenant.mapProvider;
      else userProfile.mapProvider = DEFAULT_MAP_PROVIDER;
      yield put(
        actions.loginSuccess({
          user: {
            userProfile: userProfile,
            userPermissions: userPermissions ?? [],
            userCDNs: userCDNs,
            userPreferences,
          },
          tenant: tenant,
          refresh: refresh,
        }),
      );
    } else {
      yield put(actions.logoutSuccess());
    }
  } catch (error) {
    console.error(error.message);
    yield put(actions.loginError(undefined));
  }
}

// Function to refresh the token and return the new user profile
export function* refreshToken(tenantId: string) {
  try {
    console.log('Refreshing token...');

    // Get the current authenticated user
    let userProfile: UserProfile = yield call(Auth.getAuthenticatedUser);

    if (!userProfile) {
      console.error('No authenticated user found during token refresh');
      return null;
    }

    // Fetch tenant and user permissions with the current token
    const [tenant, userPermissions, userCDNs, userPreferences] = yield call(
      fetchTenantAndUserPermissions,
      userProfile,
      tenantId,
    );

    if (userProfile) {
      userProfile.tenantId = tenant?.tenantId ?? '';
      userProfile.operatorExtension = LocalPreferences.getString(
        LocalPreference.LOCAL_PROFILE_OPT_EXTENSION_TOKEN,
      );

      if (tenant && tenant.mapProvider)
        userProfile.mapProvider = tenant.mapProvider;
      else userProfile.mapProvider = DEFAULT_MAP_PROVIDER;

      // Update the Redux store with the new user profile
      yield put(
        actions.loginSuccess({
          user: {
            userProfile: userProfile,
            userPermissions: userPermissions ?? [],
            userCDNs: userCDNs,
            userPreferences,
          },
          tenant: tenant,
          refresh: true,
        }),
      );

      console.log('Token refreshed successfully');
      return userProfile;
    } else {
      console.error('Failed to refresh token - no user profile');
      return null;
    }
  } catch (error) {
    console.error('Error refreshing token:', error);
    return null;
  }
}

// Keeps operator connection alive for connected queries.
export function* sendOperatorPulse(
  action: PayloadAction<{
    id: string;
    tenantId: string;
    accessToken: string;
  }>,
) {
  try {
    console.log('Calling Operator Pulse..');

    let loggedOnQueryOperator = action.payload;
    let url: string = `${BASE_URL_QUERY_OPERATOR}/${loggedOnQueryOperator.tenantId}/${loggedOnQueryOperator.id}/pulse`;

    let requestHeaders: Headers = new Headers();
    requestHeaders.set('Ocp-Apim-Subscription-Key', API_KEY as string);
    requestHeaders.append('Content-Type', 'application/json;charset=UTF-8');
    requestHeaders.set('Accept', 'application/json');
    requestHeaders.set(
      'Authorization',
      `Bearer ${loggedOnQueryOperator.accessToken}`,
    );

    const requestInit: RequestInit = {
      method: 'post',
      headers: requestHeaders,
    };

    const response = yield call(request, url, requestInit, true);

    // Check if we got an error response
    if (response && 'err' in response) {
      const err = response.err as ResponseError;

      // Handle 401 Unauthorized error
      if (err.response.status === 401) {
        console.log('Received 401 error, attempting to refresh token...');

        try {
          // Call the refreshToken saga and wait for it to complete
          const refreshedUserProfile = yield call(
            refreshToken,
            loggedOnQueryOperator.tenantId,
          );

          if (refreshedUserProfile && refreshedUserProfile.accessToken) {
            console.log('Token refreshed, retrying operator pulse...');

            // Update the authorization header with the new token
            requestHeaders.set(
              'Authorization',
              `Bearer ${refreshedUserProfile.accessToken}`,
            );

            const retryRequestInit: RequestInit = {
              method: 'post',
              headers: requestHeaders,
            };

            // Retry the request with the new token
            const retryResponse = yield call(request, url, retryRequestInit);

            console.log('Retry response:', retryResponse);

            // Check if the response is successful
            if (retryResponse && !('err' in retryResponse)) {
              console.log('Operator pulse successful after token refresh');
              yield put(actions.setOperatorPulseSuccess({ error: false }));
              return; // Exit the function here to prevent logout
            } else {
              console.error('Retry failed with response:', retryResponse);
            }
          }

          // If we reach here, the token refresh didn't help or we couldn't get a new token
          console.error('Token refresh failed or retry failed, logging out...');
          yield put(actions.logout());
        } catch (refreshError) {
          console.error('Error during token refresh:', refreshError);
          yield put(actions.logout());
        }
      }

      yield put(actions.setOperatorPulseSuccess({ error: true }));
    } else {
      yield put(actions.setOperatorPulseSuccess({ error: false }));
    }
  } catch (error) {
    console.error(error);
    yield put(actions.setOperatorPulseSuccess({ error: true }));
  }
}

function* unregisterUser(logoutRequest: LogoutRequest) {
  try {
    if (
      logoutRequest &&
      logoutRequest.tenantId &&
      logoutRequest.operatorId &&
      logoutRequest.accessToken
    ) {
      let requestHeaders: Headers = new Headers();
      requestHeaders.set('Ocp-Apim-Subscription-Key', API_KEY as string);
      requestHeaders.append('Content-Type', 'application/json;charset=UTF-8');
      requestHeaders.set('Accept', 'application/json');
      requestHeaders.set(
        'Authorization',
        `Bearer ${logoutRequest.accessToken}`,
      );

      let url: string =
        BASE_URL_QUERYOPERATOR +
        logoutRequest.tenantId +
        '/' +
        logoutRequest.operatorId;

      const requestInit: RequestInit = {
        method: 'delete',
        headers: requestHeaders,
      };

      yield call(request, url, requestInit);
    }
  } catch (error) {
    console.error(error.message);
  }
}

// Handling the side effects when login success
function* registerTokenExpiryHandler(action: PayloadAction<UserWithTenant>) {
  yield delay(action.payload.user.userProfile.expiresOn * 1000 - Date.now());
  yield put(actions.logout());
}

function* setUserPreferences(
  action: PayloadAction<{
    tenantId: string;
    operatorEmail: string;
    accessToken: string;
    userPreferences: WithNull<Partial<UserPreferences>>;
  }>,
) {
  try {
    const { tenantId, operatorEmail, accessToken, userPreferences } =
      action.payload;
    if (!tenantId || !operatorEmail || !accessToken) return undefined;
    // We need to do two requests here. A POST req to update any preferences and then a separate PATCH req to delete any preferences set to null.
    let requestHeaders: Headers = new Headers();
    requestHeaders.set('Ocp-Apim-Subscription-Key', API_KEY as string);
    requestHeaders.append('Content-Type', 'application/json;charset=UTF-8');
    requestHeaders.set('Accept', 'application/json');
    requestHeaders.set('Authorization', `Bearer ${accessToken}`);

    let url: string = `${BASE_URL_USER}${tenantId}/${encodeURIComponent(
      operatorEmail,
    )}/userpreferences`;

    const body = Object.keys(userPreferences).reduce((acc, curr) => {
      acc[curr] =
        typeof userPreferences[curr] === 'object' &&
        userPreferences[curr] !== null // Do not stringify null values
          ? JSON.stringify(userPreferences[curr])
          : userPreferences[curr];
      return acc;
    }, {});

    const requestInit: RequestInit = {
      method: 'post',
      headers: requestHeaders,
      body: JSON.stringify(body),
    };

    yield call(request, url, requestInit);

    // Now do the patch request to delete any preferences set to null
    const prefsToDelete = Object.fromEntries(
      Object.entries(userPreferences)
        .filter(([_, v]) => v === null)
        .map(([k]) => [k, 'reset']), // We need to set a value for the keys we want reset so we use 'reset' as a placeholder
    );

    if (Object.keys(prefsToDelete).length === 0) return; // Nothing to delete

    const patchRequestInit: RequestInit = {
      method: 'PATCH',
      headers: requestHeaders,
      body: JSON.stringify(prefsToDelete),
    };

    url = url + '/delete';

    yield call(request, url, patchRequestInit);
  } catch (error) {
    console.error(error.message);
  }
}

export function* authSaga() {
  yield takeLatest(actions.login, login);
  yield takeLatest(actions.logout, logout);
  yield takeLatest(actions.checkAuth, checkAuth);
  yield takeLatest(actions.loginSuccess, registerTokenExpiryHandler);
  yield takeLatest(actions.setOperatorPulse, sendOperatorPulse);
  yield takeLatest(actions.updateUserPreferences, setUserPreferences);
}

function* fetchTenantAndUserPermissions(
  userProfile: UserProfile,
  tenantId: string | undefined,
) {
  // Fetch user permissions from identity api service
  let identityResult:
    | {
        preferences: null | UserPreferences;
        permissions: UserClaim[];
        usercdns: UserCDN[];
        access_token: string;
        userID: string;
        timestamp: number;
        expires_in: number;
      }
    | undefined = undefined;
  if (userProfile) {
    yield put(actions.setFetchingPermissions({ fetching: true }));
    identityResult = yield call(request, BASE_URL_IDENTITY + 'connect/token', {
      method: 'post',
      headers: {
        'Ocp-Apim-Subscription-Key': API_KEY,
        'Access-Token': userProfile.accessToken,
      },
      body: new URLSearchParams({
        client_id: 'multi_tenant_client',
        client_secret: 'client_secret',
        grant_type: 'delegation',
        scope: 'openid',
        tenant_id: tenantId ?? '',
        token: userProfile.accessToken,
        issuer:
          TOKEN_ISS?.replace('{organizationId}', userProfile.organizationId) ??
          '',
        audience: TOKEN_AUD ?? '',
      }),
    } as any);
  }

  if (!identityResult) {
    throw new NotAuthorisedError('No identity details found');
  }

  const userPermissions = identityResult.permissions
    ? identityResult.permissions.map(c => new NewUserPermission(c))
    : [];
  userProfile.accessToken = identityResult.access_token;
  userProfile.id = identityResult.userID;
  userProfile.expiresOn = identityResult.expires_in + identityResult.timestamp;
  const userPreferences: UserPreferences =
    LocalPreferences.populateUserPreferences(identityResult.preferences ?? {});
  let tenant: Tenant | undefined;

  if (tenantId === 'admin' || tenantId === undefined) tenant = undefined;
  else
    tenant = userProfile
      ? yield call(request, BASE_URL_TENANT + '/tenant/' + tenantId, {
          method: 'get',
          headers: {
            'Content-Type': 'application/json;charset=UTF-8',
            Accept: 'application/json',
            'Ocp-Apim-Subscription-Key': API_KEY,
            Authorization: `Bearer ${userProfile.accessToken}`,
          },
        } as any)
      : undefined;

  yield put(actions.setFetchingPermissions({ fetching: false }));

  return [tenant, userPermissions, identityResult.usercdns, userPreferences];
}
